// Content-widget primitives used by every guide page.
//
// Kept in one file because they're all small, related, and almost always
// imported together. Splitting them would just add import noise.

import type { ReactNode } from 'react';
import type { CalloutType, WithChildren } from '../types';

// ===================================================================
// Callout
// ===================================================================

interface CalloutProps {
  /** Severity / colour variant. Defaults to 'info'. */
  type?: CalloutType;
  /** Emoji or single character shown on the left. Defaults per `type`. */
  icon?: string;
  /** Bold heading at the top of the callout. */
  title?: string;
  /** Body content — typically one or more <p> elements. */
  children: ReactNode;
}

// Default icons keyed by callout type. Override by passing the `icon` prop.
const DEFAULT_ICONS: Record<CalloutType, string> = {
  info:    '📘',
  tip:     '💡',
  warning: '⚠️',
  note:    '📝',
};

export function Callout({ type = 'info', icon, title, children }: CalloutProps) {
  return (
    <div className={`callout callout--${type}`}>
      <span className="callout__icon">{icon ?? DEFAULT_ICONS[type]}</span>
      <div className="callout__body">
        {title && <strong>{title}</strong>}
        {children}
      </div>
    </div>
  );
}

// ===================================================================
// Steps
// ===================================================================

/** Renders an ordered list with the chunky brand-coloured step numbers. */
export function Steps({ children }: WithChildren) {
  return <ol className="steps">{children}</ol>;
}

// ===================================================================
// FAQItem
// ===================================================================

interface FAQItemProps {
  /** The text always shown at the top of the collapsible. */
  question: string;
  /** The answer revealed when the user expands it. */
  children: ReactNode;
}

export function FAQItem({ question, children }: FAQItemProps) {
  return (
    <details className="faq-item">
      <summary className="faq-item__question">{question}</summary>
      <div className="faq-item__answer">{children}</div>
    </details>
  );
}

// ===================================================================
// ScreenshotPlaceholder
// ===================================================================

interface ScreenshotPlaceholderProps {
  /** What the screenshot should depict (the "Show: …" text). */
  description: string;
  /** Suggested file path for the screenshot, shown in monospace. */
  filename: string;
}

export function ScreenshotPlaceholder({
  description,
  filename,
}: ScreenshotPlaceholderProps) {
  return (
    <div className="screenshot-placeholder">
      <span className="screenshot-placeholder__label">📸 Screenshot needed</span>
      <span className="screenshot-placeholder__desc">Show: {description}</span>
      Suggested filename: <code>{filename}</code>
    </div>
  );
}

// ===================================================================
// Figure
// ===================================================================

interface FigureProps {
  /** Image path, e.g. '/screenshots/teacher/attendance.png'. */
  src: string;
  /** REQUIRED alt text describing the screenshot for screen readers. */
  alt: string;
  /** Optional italic caption shown beneath the image. */
  caption?: string;
}

export function Figure({ src, alt, caption }: FigureProps) {
  return (
    <figure className="screenshot">
      <img src={src} alt={alt} />
      {caption && <figcaption>{caption}</figcaption>}
    </figure>
  );
}
